import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface LayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  userName?: string;
  onLogout?: () => void;
}

export function Layout({ children, showHeader = false, userName, onLogout }: LayoutProps) {
  return (
    <div className="min-h-screen bg-[#f3ede3] font-[Inter]">
      {showHeader && (
        <header className="fixed top-0 left-0 right-0 z-40 bg-[#f3ede3] border-b border-[#e3dbcf]">
          <div className="px-6 lg:px-10">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-end space-x-4">
                <Link to="/dashboard" className="flex items-center">
                  <img src="/logo-light.png" alt="REWagent" className="h-10 w-auto" />
                </Link>
                {userName && (
                  <span className="text-sm text-[#6b635a] pb-0.5">
                    Welcome, <span className="text-[#1a1a1a] font-semibold">{userName}</span>
                  </span>
                )}
              </div>

              <div className="flex items-center space-x-5">
                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="text-sm text-[#8c8578] hover:text-[#1a1a1a] transition-colors font-medium"
                  >
                    Sign out
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>
      )}
      <main className={showHeader ? 'pt-16' : ''}>
        {children}
      </main>
    </div>
  );
}

interface AdminLayoutProps {
  children: ReactNode;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onLogout?: () => void;
}

export function AdminLayout({ children, activeTab, onTabChange, onLogout }: AdminLayoutProps) {
  const tabs = [
    { id: 'clients', label: 'Clients', icon: '🏢' },
    { id: 'access-codes', label: 'Access Codes', icon: '🔑' },
    { id: 'leads', label: 'Leads', icon: '📊' },
    { id: 'campaigns', label: 'Campaigns', icon: '📢' },
    { id: 'logs', label: 'Logs', icon: '📋' },
  ];

  return (
    <div className="h-screen bg-[#f3ede3] flex font-[Inter]">
      {/* Sidebar */}
      <aside className="w-64 bg-[#fcfbfa] border-r border-[#e3dbcf] flex flex-col">
        <div className="p-6 border-b border-[#e3dbcf]">
          <div className="flex items-center">
            <img src="/logo-light.png" alt="REWagent" className="h-10 w-auto" />
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange?.(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                ? 'bg-[#1a1a1a] text-[#f3ede3]'
                : 'text-[#6b635a] hover:bg-[#e3dbcf]/30 hover:text-[#1a1a1a]'
                }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="px-4 pb-2">
          <button
            onClick={() => onTabChange?.('leads?add=true')}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#1a1a1a] hover:bg-[#000000] text-[#f3ede3] rounded-lg text-sm font-medium transition-all"
          >
            <span>＋</span> Add Lead
          </button>
        </div>

        <div className="p-4 border-t border-[#e3dbcf]">
          <button
            onClick={onLogout}
            className="w-full px-4 py-3 text-sm text-[#8c8578] hover:text-[#1a1a1a] hover:bg-[#e3dbcf]/30 rounded-lg transition-all text-left font-medium"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-[#f3ede3]">
        {children}
      </main>
    </div>
  );
}
